import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import AddForm from "../components/AddForm";
import UpdateForm from "../components/UpdateForm";

// RasPm Dashboard examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { RequestService } from "services/RequestService";

function STDataTables() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState({
    columns: [
      {
        Header: "#",
        accessor: "index",
        width: "5%",
        sortable: true,
        align: "center",
      },
      {
        Header: "اسم التوربين",
        accessor: "name",
        width: "25%",
        sortable: true,
      },
      {
        Header: "المحطة",
        accessor: "powerStationName",
        width: "30%",
        sortable: true,
      },
      {
        Header: "عدد التوربينات الغازية",
        accessor: "gtCount",
        width: "25%",
        sortable: true,
      },
      {
        Header: "اجراء",
        accessor: "actions",
        width: "15%",
        sortable: false,
      }
    ],
    rows: [],
  });

  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "تنبيه",
    content: "",
    dateTime: "",
    open: false,
  });

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [stToDelete, setStToDelete] = useState(null);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [stToUpdate, setStToUpdate] = useState(null);
  
  const closeNotification = () => setNotification({ ...notification, open: false });

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleOpenUpdateDialog = (st) => {
    setStToUpdate(st);
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setStToUpdate(null);
  };

  const handleOpenDeleteDialog = (stId) => {
    setStToDelete(stId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setStToDelete(null);
  };

  const handleDelete = async () => {
    try {
      const response = await RequestService.delete(`ST/${stToDelete}`);
      if (response.data && !response.data.error) {
        setNotification({
          color: "success",
          icon: "check",
          title: "تمت العملية بنجاح",
          content: response.data?.message || "تم حذف التوربين البخاري بنجاح",
          open: true,
        });
        fetchSTs();
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في حذف التوربين البخاري",
          open: true,
        });
      }
    } catch (error) {
      console.error("Error deleting steam turbine:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء حذف التوربين البخاري",
        open: true,
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleUpdate = async (st) => {
    try {
      const response = await RequestService.put(`ST/${st.id}`, st);
      if (response.data && !response.data.error) {
        setNotification({
          color: "success",
          icon: "check",
          title: "تمت العملية بنجاح",
          content: response.data?.message || "تم تعديل التوربين البخاري بنجاح",
          open: true,
        });
        fetchSTs();
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تعديل التوربين البخاري",
          open: true,
        });
      }
    } catch (error) {
      console.error("Error updating steam turbine:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء تعديل التوربين البخاري",
        open: true,
      });
    } finally {
      handleCloseUpdateDialog();
    }
  };

  const fetchSTs = async () => {
    setIsLoading(true);
    try {
      const response = await RequestService.get("ST");
      if (response.data && !response.data.error) {
        const sts = response.data.data || [];
        setTableData({
          ...tableData,
          rows: sts.map((st, index) => ({
            index: index + 1,
            name: st.name,
            powerStationName: st.powerStationName,
            gtCount: st.gtCount,
            actions: (
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => handleOpenUpdateDialog(st)}
                  startIcon={<Icon>edit</Icon>}
                >
                  تعديل
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleOpenDeleteDialog(st.id)}
                  startIcon={<Icon>delete</Icon>}
                >
                  حذف
                </MDButton>
              </MDBox>
            ),
          })),
        });
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تحميل بيانات التوربينات البخارية",
          open: true,
        });
      }
    } catch (error) {
      console.error("Error fetching steam turbines:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء جلب بيانات التوربينات البخارية",
        open: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSTs();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  backgroundColor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    قائمة التوربينات البخارية
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    إدارة التوربينات البخارية وربطها بالمحطات
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleOpenAddDialog}
                  startIcon={<AddIcon />}
                >
                  إضافة توربين
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={tableData}
                  isSorted={true}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <AddForm
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        onSuccess={(message) => {
          fetchSTs();
          handleCloseAddDialog();
          setNotification({
            color: "success",
            icon: "check",
            title: "تمت العملية بنجاح",
            content: message || "تم إضافة التوربين البخاري بنجاح",
            open: true,
          });
        }}
      />
      <UpdateForm
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        st={stToUpdate}
        onSuccess={(st) => handleUpdate(st)}
      />
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"تأكيد الحذف"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            هل أنت متأكد من حذف هذا التوربين البخاري؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleDelete} color="error" autoFocus>
            حذف
          </MDButton>
          <MDButton onClick={handleCloseDeleteDialog} color="secondary">
            إلغاء
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
      />
    </DashboardLayout>
  );
}

export default STDataTables;
